import React, { useEffect, useRef, useMemo } from 'react';

/**
 * Local development stub for @user-interfaces/evo-app-container.
 *
 * In production the Evo shell provides this module via its import map.
 * During local development this stub is resolved instead (see the Vite
 * resolve alias in vite.config.ts and the resolveId plugin in
 * app/vite.config.ts).
 *
 * The stub renders the underlying oc-component directly by building the
 * OC registry URL from the componentParams, replicating the behaviour of
 * the real EvoMfeApp so the widget is visible during local development.
 */

export interface EvoMfeAppProps {
  componentName: string;
  componentVersion: string;
  componentParams?: Record<string, unknown>;
  className?: string;
}

/** OC Client script - loaded once globally. */
const OC_CLIENT_URL = 'https://mfe.workspace.accessacloud.com/oc-client/client.js';

function ensureOcClient(): void {
  if (document.querySelector(`script[src="${OC_CLIENT_URL}"]`)) return;
  const script = document.createElement('script');
  script.src = OC_CLIENT_URL;
  script.async = true;
  document.head.appendChild(script);
}

export const EvoMfeApp: React.FC<EvoMfeAppProps> = ({
  componentName,
  componentVersion,
  componentParams,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureOcClient();
  }, []);

  // Serialise componentParams so the effect only fires when values actually
  // change, not on every render that creates a new object reference.
  const serialisedParams = useMemo(
    () => (componentParams ? JSON.stringify(componentParams, Object.keys(componentParams).sort()) : ''),
    [componentParams],
  );

  // Track the last href we rendered so we can skip no-op re-renders that
  // would destroy and recreate the shadow-DOM host (causing the glitch).
  const prevHrefRef = useRef<string>('');

  useEffect(() => {
    if (!containerRef.current) return;

    const params = new URLSearchParams();
    if (serialisedParams) {
      const parsed = JSON.parse(serialisedParams) as Record<string, unknown>;
      for (const [key, value] of Object.entries(parsed)) {
        if (value !== undefined && value !== null) {
          params.set(key, String(value));
        }
      }
    }

    const baseUrl = `https://mfe.workspace.accessacloud.com/registry/${componentName}/${componentVersion}/`;
    const href = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    // Skip re-render when the resolved URL hasn't changed – avoids
    // destroying the existing oc-component (and its shadow root), which
    // caused a visible layout glitch during timesheet week navigation.
    if (href === prevHrefRef.current) return;
    prevHrefRef.current = href;

    // Render an <oc-component> element so the OC client picks it up.
    containerRef.current.innerHTML = '';
    const oc = document.createElement('oc-component');
    oc.setAttribute('href', href);
    containerRef.current.appendChild(oc);

    // If the OC client is already loaded, tell it to render the new component.
    if (typeof (window as any).oc !== 'undefined') {
      (window as any).oc.renderUnloadedComponents();
    }
  }, [componentName, componentVersion, serialisedParams]);

  return (
    <div
      ref={containerRef}
      data-evo-mfe-app
      data-component-name={componentName}
      data-component-version={componentVersion}
      className={className}
    />
  );
};
